import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiDelete, apiGet } from '@/lib/http/client';

export interface RecallDevicePermissions {
  microphone: boolean;
  system_audio: boolean;
  accessibility: boolean;
}

export interface RecallDevice {
  device_id: string;
  device_name: string;
  platform: 'mac' | 'windows';
  app_version: string | null;
  last_seen_at: string | null;
  recording_enabled: boolean;
  permissions: RecallDevicePermissions;
  status: 'active' | 'revoked';
}

interface DevicesResponse {
  devices: RecallDevice[];
}

function recallDevicesQueryKey(orgId: number | undefined) {
  return ['recall', 'devices', orgId] as const;
}

/**
 * Fetches the list of registered Ahoy Desktop devices for the org.
 * Refetches when the window regains focus so users coming back to the tab
 * see fresh state without manual reload. 30s staleTime prevents refetches
 * for rapid focus toggles (matches the desktop heartbeat cadence).
 */
export function useRecallDevices(
  orgId: number | undefined
): UseQueryResult<RecallDevice[]> {
  return useQuery({
    queryKey: recallDevicesQueryKey(orgId),
    queryFn: async () => {
      try {
        const data = await apiGet<DevicesResponse>(
          `/api/orgs/${orgId}/recall/devices/`
        );
        return data.devices;
      } catch (err) {
        // 404 means the devices endpoint is unavailable for this org (e.g.,
        // feature not provisioned). Treat as "no devices" rather than a hard
        // query error so the UI degrades gracefully instead of showing an
        // error state. Other status codes propagate normally.
        const { statusCode } = err as { statusCode?: number };
        if (statusCode === 404) {
          return [];
        }
        throw err;
      }
    },
    enabled: orgId !== undefined,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

/**
 * Revokes a desktop device. The mutation optimistically removes the device
 * from the cached list. On error, the previous list is restored. On success,
 * the list is invalidated so any in-flight server state replaces the optimistic
 * state.
 */
export function useRevokeDevice(
  orgId: number | undefined
): UseMutationResult<void, Error, string, { previous?: RecallDevice[] }> {
  const queryClient = useQueryClient();
  const key = recallDevicesQueryKey(orgId);
  return useMutation({
    mutationFn: async (deviceId: string) => {
      if (orgId === undefined) {
        throw new Error('orgId is required to revoke a device');
      }
      await apiDelete(`/api/orgs/${orgId}/recall/devices/${deviceId}/`);
    },
    onMutate: async deviceId => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<RecallDevice[]>(key);
      if (previous !== undefined) {
        queryClient.setQueryData<RecallDevice[]>(
          key,
          previous.filter(d => d.device_id !== deviceId)
        );
      }
      return { previous };
    },
    onError: (_err, _deviceId, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData<RecallDevice[]>(key, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
